// The "Merlin" family — single source of truth for the Agent-SDK assistants a
// portal user reaches from the top bar.
//
// Add a new Merlin by adding ONE entry here: the nav switcher (and any other
// consumer) derives its label, icon, route and access gate from this registry —
// nothing about the family is hard-coded in the navbar.
//
// These are DISPLAY strings + wiring. `route` and `scope` MUST match the real
// door (App.tsx route guards + app/auth/scopes.py); renaming those is a
// breaking backend change and is intentionally out of scope for branding.
//
// NOTE: the Tech-Support door (/portal/tech-support-copilot) is deliberately NOT
// a Merlin-family member here. Its page is an email/inbox WORKSPACE with Merlin
// embedded as one panel, so the top bar surfaces it as its own plain "Tech
// Support" tab in PortalNavbar — not as a peer in this switcher. Merlin stays
// branded inside that page. Don't re-add it here without that context.

export type MerlinId = 'analyst' | 'quality';

export interface MerlinSpecialist {
  /** Stable id — never shown to users. */
  id: MerlinId;
  /** One-word role/"hat", shown as a chip beside the Merlin wordmark. */
  role: string;
  /** Full written/spoken name, e.g. "Merlin Analyst" (a11y label + tooltip). */
  fullName: string;
  /** Portal route this Merlin lives at (must match App.tsx / the backend door). */
  route: string;
  /** Scope that grants access (must match app/auth/scopes.py). */
  scope: string;
  /** A second scope that opens the same door read-only (the Quality door's
   *  quality:viewer, kanban 808dabf6). */
  viewerScope?: string;
  /** Accent colour for the mark + role chip. */
  accent: string;
  /** One-line description shown in the switcher dropdown. */
  blurb: string;
  /** Short capability note shown under the blurb. */
  scopeNote: string;
}

// Registry order = the order specialists appear in the switcher.
export const MERLINS: readonly MerlinSpecialist[] = [
  {
    id: 'analyst',
    role: 'Analyst',
    fullName: 'Merlin Analyst',
    route: '/portal/analyst',
    scope: 'analyst:copilot',
    accent: '#3b82f6',
    blurb: 'Ask about orders, customers & the business',
    scopeNote: 'Read-only data',
  },
  {
    id: 'quality',
    role: 'Quality',
    fullName: 'Merlin Quality',
    route: '/portal/quality-copilot',
    scope: 'quality:copilot',
    viewerScope: 'quality:viewer',
    accent: '#a855f7',
    blurb: 'Voice: log signals & draft orders hands-free',
    scopeNote: 'Voice · can draft orders',
  },
] as const;

/** The family wordmark shown in the nav (the role chip differentiates). */
export const MERLIN_WORDMARK = 'Merlin';

/** Family accent — the violet the collapsed switcher uses to represent all. */
export const MERLIN_FAMILY_ACCENT = '#7c6cf0';

/**
 * The family wizard avatar. The SAME logo for every Merlin — the per-Merlin
 * accent (ring) and role chip are what differentiate the specialists. Served
 * statically from ``frontend/public/`` at this root-relative path.
 */
export const MERLIN_AVATAR = '/merlin/merlin.png';

/** Merlins the current user can reach, in registry order. */
export function accessibleMerlins(
  hasScope: (scope: string) => boolean,
): MerlinSpecialist[] {
  return MERLINS.filter(
    (m) => hasScope(m.scope) || (m.viewerScope !== undefined && hasScope(m.viewerScope)),
  );
}

/** The Merlin owning a given path, if any (matches the route and its sub-paths). */
export function activeMerlin(
  pathname: string,
  merlins: readonly MerlinSpecialist[] = MERLINS,
): MerlinSpecialist | null {
  return (
    merlins.find(
      (m) => pathname === m.route || pathname.startsWith(m.route + '/'),
    ) ?? null
  );
}
